import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { trackEvent } from '../utils/analytics';
import ProgressStepper from './ProgressStepper';

const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

function DocumentUpload() {
  const { id } = useParams(); // Application ID
  const navigate = useNavigate();
  const location = useLocation();
  const signedRedirect = new URLSearchParams(location.search).get('signed') === '1';
  const [files, setFiles] = useState({});
  const [error, setError] = useState(null);
  const [uploadStatus, setUploadStatus] = useState({});
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState('identity');
  const [isMobile, setIsMobile] = useState(false);
  const [requiresLogin, setRequiresLogin] = useState(false);
  const [identityLoading, setIdentityLoading] = useState(false);
  const [tuReport, setTuReport] = useState(null);
  const [tuLoading, setTuLoading] = useState(false);
  const stepPanelRef = useRef(null);
  const hasInitializedStep = useRef(false);

  const fetchApplication = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setRequiresLogin(true);
        setLoading(false);
        return;
      }
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/applications/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Could not fetch application details');
      const data = await response.json();
      setApplication(data.application || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchTuReport = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenant/tu-reports/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTuReport(data.report);
      }
    } catch (err) {
      console.warn('TU report fetch:', err.message);
    }
  }, [id]);

  useEffect(() => {
    fetchApplication();
    fetchTuReport();
  }, [fetchApplication, fetchTuReport]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tu_purchase') === 'success') {
      // Navigate to credit step and refresh TU status
      setCurrentStep('credit');
      hasInitializedStep.current = true; // Prevent auto-advance from overriding
      fetchTuReport();
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('tu_purchase') === 'cancel') {
      setCurrentStep('credit');
      hasInitializedStep.current = true;
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fetchTuReport]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const documentTypes = [
    { id: 'government_id', label: 'Government ID' },
    { id: 'employment_letter', label: 'Letter of Employment' },
    { id: 'paystub', label: 'Most Recent Paystub' },
    { id: 'reference_letter', label: 'Reference Letter' },
    { id: 'bank_statement', label: 'Bank Statement' },
    { id: 'credit_report', label: 'Credit Report' },
  ];

  const getDocsForType = useCallback(
    (docType) => (application?.documents || []).filter((doc) => doc.document_type === docType),
    [application]
  );

  const leaseDocs = application?.leases || [];
  const leaseReady = leaseDocs.length > 0;
  const leaseSigned = leaseDocs.some((doc) => ['tenant_signed', 'signed', 'completed'].includes(doc.status) || doc.signed_at);
  const landlordSigned = leaseDocs.some((doc) => ['landlord_signed', 'sent'].includes(doc.status));

  const documentSteps = useMemo(() => {
    const steps = [
      {
        key: 'identity',
        title: 'Identity Verification',
        description: 'Verify your identity with a government ID and selfie. This is a quick, secure check powered by Stripe.',
        docs: [],
        optionalDocs: []
      },
      {
        key: 'income',
        title: 'Income & Employment',
        description: 'Add income proof and employment documentation.',
        docs: ['employment_letter', 'paystub', 'bank_statement'],
        optionalDocs: ['bank_statement']
      },
      {
        key: 'references',
        title: 'References',
        description: 'Provide a reference letter if you have one.',
        docs: ['reference_letter'],
        optionalDocs: ['reference_letter']
      },
      {
        key: 'credit',
        title: 'Credit Report',
        description: 'Upload a recent credit report for screening review.',
        docs: ['credit_report'],
        optionalDocs: []
      }
    ];

    if (leaseReady) {
      steps.push({
        key: 'lease',
        title: 'Lease Signing',
        description: 'Sign the lease online or upload a signed copy if you completed it offline.',
        docs: [],
        optionalDocs: []
      });
    }

    return steps;
  }, [leaseReady]);

  const getVerificationBadge = () => {
    if (!application) return { label: 'Unverified landlord', tone: 'warning' };
    if (application.landlord_ownership_status === 'approved' || application.landlord_id_status === 'verified') {
      return { label: 'Verified landlord', tone: 'paid' };
    }
    return { label: 'Unverified landlord', tone: 'warning' };
  };

  const handleFileChange = (e) => {
    const { name, files: selectedFiles } = e.target;
    const file = selectedFiles && selectedFiles[0] ? selectedFiles[0] : null;
    setFiles(prev => ({ ...prev, [name]: file }));
    setError(null);
    setUploadStatus(prev => ({ ...prev, [name]: '' }));
    if (file) {
      handleUpload(name, file);
    }
  };

  const handleUpload = async (documentType, fileOverride = null) => {
    const file = fileOverride || files[documentType];
    if (!file) {
      setError(`Please select a file for ${documentType}.`);
      return;
    }

    const formData = new FormData();
    formData.append('documents', file);
    formData.append('document_type', documentType);

    const token = localStorage.getItem('token');

    try {
      setUploadStatus(prev => ({ ...prev, [documentType]: 'Uploading...' }));
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/applications/${id}/documents`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Upload failed');
      }

      setUploadStatus(prev => ({ ...prev, [documentType]: 'Uploaded ✓' }));
      await fetchApplication();

      trackEvent({
        event_name: 'document_uploaded',
        event_category: 'application',
        event_action: documentType,
        application_id: Number(id),
        metadata: { document_type: documentType }
      });
    } catch (err) {
      setError(err.message);
      setUploadStatus(prev => ({ ...prev, [documentType]: 'Failed ✗' }));
    }
  };

  const handleDownload = (docId) => {
    const token = localStorage.getItem('token');
    if (!docId || !token) return;
    window.open(`${process.env.REACT_APP_API_BASE_URL}/api/documents/${docId}?token=${token}`, '_blank');
  };

  const handleLeaseDownload = (leaseId) => {
    const token = localStorage.getItem('token');
    if (!leaseId || !token) return;
    window.open(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/leases/${leaseId}/download?token=${token}`, '_blank');
  };

  const handleOpenEsign = async (leaseId) => {
    const token = localStorage.getItem('token');
    if (!leaseId || !token) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/leases/${leaseId}/esign/url?role=tenant`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const message = data.error || 'Failed to open signing link';
        if (String(message).toLowerCase().includes('completed')) {
          window.alert('This document has already been completed.');
          setError(null);
          return;
        }
        throw new Error(message);
      }
      if (data.url) {
        window.open(data.url, '_blank');
        return;
      }
      throw new Error('Signing link not ready yet. Ask your landlord to start e-sign.');
    } catch (err) {
      setError(err.message || 'Failed to open signing link');
    }
  };

  const handleDelete = async (docId, docType) => {
    if (!docId) return;
    if (!window.confirm('Delete this document?')) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/applications/${id}/documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to delete document');
      setApplication((prev) => {
        if (!prev) return prev;
        return { ...prev, documents: (prev.documents || []).filter((doc) => doc.id !== docId) };
      });
      setUploadStatus(prev => ({ ...prev, [docType]: '' }));
    } catch (err) {
      setError(err.message);
    }
  };


  const stepKeys = documentSteps.map((step) => step.key);
  const stepIndex = stepKeys.indexOf(currentStep);
  const nextStep = documentSteps[stepIndex + 1];
  const prevStep = documentSteps[stepIndex - 1];

  const isDocUploaded = useCallback((docType) => getDocsForType(docType).length > 0, [getDocsForType]);

  const handleStepChange = (key) => {
    if (!key) return;
    setCurrentStep(key);
    window.setTimeout(() => {
      stepPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };
  const isStepComplete = useCallback((step) => {
    if (step.key === 'lease') return leaseSigned;
    if (step.key === 'identity') return application?.identity_status === 'verified';
    if (step.key === 'credit') return tuReport?.status === 'completed' || isDocUploaded('credit_report');
    const requiredDocs = step.docs.filter((doc) => !step.optionalDocs?.includes(doc));
    if (requiredDocs.length === 0) {
      return step.docs.some((doc) => isDocUploaded(doc));
    }
    return requiredDocs.every((doc) => isDocUploaded(doc));
  }, [leaseSigned, isDocUploaded, application, tuReport]);

  const allRequiredComplete = useMemo(
    () => documentSteps.filter((step) => step.key !== 'lease').every((step) => isStepComplete(step)),
    [documentSteps, isStepComplete]
  );

  const getStepStatus = useCallback((step) => {
    if (step.key === 'lease') {
      if (!leaseReady) return 'pending';
      if (leaseSigned) return 'approved';
      if (landlordSigned) return 'needs_info';
      return 'pending';
    }
    const hasAnyDoc = (step.docs || []).some((doc) => isDocUploaded(doc));
    if (isStepComplete(step)) return 'approved';
    return hasAnyDoc ? 'needs_info' : 'pending';
  }, [isDocUploaded, isStepComplete, leaseReady, leaseSigned, landlordSigned]);

  const stepperSteps = useMemo(() => documentSteps.map((step) => ({
    key: step.key,
    label: step.title,
    status: getStepStatus(step)
  })), [documentSteps, getStepStatus]);

  const stepperProgress = useMemo(() => {
    if (!stepperSteps.length) return 0;
    const completed = stepperSteps.filter((step) => step.status === 'approved').length;
    return completed / stepperSteps.length;
  }, [stepperSteps]);

  const phoneUploadUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    const base = window.location.origin;
    return `${base}/application/${id}/documents?source=phone`;
  }, [id]);

  useEffect(() => {
    if (!application || hasInitializedStep.current) return;
    const firstIncomplete = documentSteps.find((step) => !isStepComplete(step));
    if (firstIncomplete) {
      setCurrentStep(firstIncomplete.key);
    }
    hasInitializedStep.current = true;
  }, [application, documentSteps, isStepComplete]);

  if (loading) {
    return <main className="App-main document-upload-page">Loading document upload...</main>;
  }

  if (requiresLogin) {
    const redirectPath = `${location.pathname}${location.search}`;
    return (
      <main className="App-main document-upload-page">
        <div className="dashboard-section" style={{ maxWidth: '520px' }}>
          <h3>Login required</h3>
          <p className="status-text" style={{ marginTop: '0.35rem' }}>
            Please sign in to continue uploading documents for this application.
          </p>
          <Link className="primary-button" to={`/login?redirect=${encodeURIComponent(redirectPath)}`}>
            Log in
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="App-main document-upload-page">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <h2 style={{ marginBottom: 0 }}>Upload Supporting Documents</h2>
        <Link to="/verification" className={`status-pill ${getVerificationBadge().tone}`} title="Learn what landlord verification means">
          {getVerificationBadge().label}
        </Link>
      </div>
      {signedRedirect && (
        <div className="dashboard-section notice-card" style={{ marginTop: '0.75rem', borderLeft: '4px solid #2f6fed', background: '#f5f8ff' }}>
          <strong>Lease signed - pending landlord approval</strong>
          <p className="status-text" style={{ marginTop: '0.5rem' }}>
            Thanks for signing. We'll let you know once the landlord completes the final approval.
          </p>
        </div>
      )}

      <p style={{ marginTop: '0.75rem' }}>Please upload the following documents to support your application.</p>

      {allRequiredComplete && (
        <div className="dashboard-section" style={{ marginTop: '0.75rem', background: '#ecfdf3', borderLeft: '4px solid #16a34a', padding: '0.75rem' }}>
          <p className="status-text" style={{ margin: 0 }}>
            ✅ All required documents submitted. Your application is now under review.
          </p>
        </div>
      )}

      {error && <p className="error-message">{error}</p>}

      <ProgressStepper
        steps={stepperSteps}
        currentKey={currentStep}
        onSelect={handleStepChange}
        progress={stepperProgress}
      />

      <div className="review-wizard" style={{ marginTop: '1.5rem' }}>
        <aside className="review-steps">
          <div className="section-header" style={{ justifyContent: 'flex-start', textAlign: 'left' }}>
            <h3>Upload steps</h3>
          </div>
          <p className="status-text" style={{ marginTop: '-0.25rem', marginBottom: '0.75rem' }}>
            Work through each section. You can jump between steps.
          </p>
          {documentSteps.map((step) => {
            const statusKey = getStepStatus(step);
            const chipClass = statusKey === 'approved'
              ? 'report-chip--success'
              : statusKey === 'needs_info'
                ? 'report-chip--warn'
                : 'report-chip--muted';
            const statusLabel = statusKey === 'approved' ? 'Complete' : statusKey === 'needs_info' ? 'In progress' : 'Pending';
            return (
              <React.Fragment key={step.key}>
                <button
                  type="button"
                  className={`review-step ${currentStep === step.key ? 'active' : ''}`}
                  onClick={() => handleStepChange(step.key)}
                >
                  <div className="review-step-header">
                    <span className="review-step-title">{step.title}</span>
                    <span className={`report-chip ${chipClass}`}>{statusLabel}</span>
                  </div>
                  <p className="status-text" style={{ marginTop: '0.35rem' }}>{step.description}</p>
                </button>
              </React.Fragment>
            );
          })}
          {!isMobile && (
            <div className="dashboard-section" style={{ marginTop: '0.75rem', background: '#f7f8fc', border: '1px solid #e6eef0', padding: '1rem' }}>
              <h4 style={{ marginTop: 0 }}>Upload from your phone</h4>
              <p className="status-text" style={{ marginTop: '0.35rem' }}>
                Scan the QR code to open this upload flow on your phone (login required).
              </p>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(phoneUploadUrl)}`}
                  alt="Upload from phone"
                  style={{ borderRadius: '12px', border: '1px solid #e6eef0' }}
                />
              </div>
            </div>
          )}
        </aside>

        <div className="review-main" ref={stepPanelRef}>
          <div key={currentStep} className="review-panel">
          {documentSteps.map((step) => (
            currentStep === step.key && (
              <div key={step.key} className="dossier-section">
                <div className="section-header" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3>{step.title}</h3>
                  <span className="report-chip report-chip--info">Step {stepIndex + 1} of {documentSteps.length}</span>
                </div>
                <p className="status-text" style={{ marginTop: '0.35rem' }}>{step.description}</p>

                {step.key === 'lease' && (
                  <div className="dashboard-section" style={{ marginTop: '1rem', background: '#f8fafc', border: '1px solid #e6eef0', padding: '1rem' }}>
                    <p className="status-text" style={{ marginTop: 0 }}>
                      {leaseSigned
                        ? '✅ Lease signed and received.'
                        : landlordSigned
                          ? 'Landlord signed. Please add your signature.'
                          : 'Waiting for landlord signature.'}
                    </p>
                    {leaseDocs.length > 0 && (
                      <ul className="document-list" style={{ marginTop: '0.5rem' }}>
                        {leaseDocs.map((doc) => (
                          <li key={doc.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <button type="button" className="link-button" onClick={() => handleLeaseDownload(doc.id)}>
                              {doc.original_filename || 'Lease document'}
                            </button>
                            {doc.status && (
                              <span className="status-text">{doc.status.replace('_', ' ')}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="form-actions" style={{ marginTop: '0.75rem', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                      {leaseDocs?.[0]?.id && (
                        <button
                          className="primary-button"
                          type="button"
                          onClick={() => {
                            if (leaseSigned) {
                              window.alert('This document has already been completed.');
                              return;
                            }
                            handleOpenEsign(leaseDocs[0].id);
                          }}
                        >
                          Sign Online
                        </button>
                      )}
                      {leaseDocs?.[0]?.id && (
                        <span className="status-text">or</span>
                      )}
                      {leaseDocs?.[0]?.id && (
                        <label className="secondary-button" htmlFor="tenant_signed_lease_upload">
                          Upload signed lease
                          <input
                            id="tenant_signed_lease_upload"
                            type="file"
                            accept="application/pdf"
                            style={{ display: 'none' }}
                            onChange={async (e) => {
                              if (leaseSigned) {
                                window.alert('This document has already been completed.');
                                return;
                              }
                              const file = e.target.files?.[0];
                              if (!file) return;
                              const formData = new FormData();
                              formData.append('lease', file);
                              const token = localStorage.getItem('token');
                              const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/leases/${leaseDocs[0].id}/upload-signed`, {
                                method: 'POST',
                                headers: { Authorization: `Bearer ${token}` },
                                body: formData
                              });
                              if (!res.ok) {
                                const data = await res.json().catch(() => ({}));
                                const message = data.error || 'Failed to upload signed lease';
                                if (String(message).toLowerCase().includes('completed')) {
                                  window.alert('This document has already been completed.');
                                  setError(null);
                                  return;
                                }
                                setError(message);
                                return;
                              }
                              fetchApplication();
                            }}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                )}

                {/* Identity verification via Stripe Identity */}
                {step.key === 'identity' && (
                  <div className="dashboard-section" style={{ marginTop: '1rem', padding: '1rem', borderLeft: `4px solid ${application?.identity_status === 'verified' ? '#16a34a' : application?.identity_status === 'pending' ? '#d97706' : '#2563eb'}` }}>
                    {application?.identity_status === 'verified' ? (
                      <div>
                        <p style={{ margin: 0, fontWeight: 500, color: '#16a34a' }}>✅ Identity verified</p>
                        <p className="status-text" style={{ marginTop: '0.25rem' }}>Your identity has been confirmed. You can proceed to the next step.</p>
                      </div>
                    ) : application?.identity_status === 'pending' ? (
                      <div>
                        <p style={{ margin: 0, fontWeight: 500, color: '#d97706' }}>⏳ Verification not completed</p>
                        <p className="status-text" style={{ marginTop: '0.25rem' }}>It looks like the verification window was closed before completing. You can try again below.</p>
                        <button
                          className="primary-button"
                          style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}
                          disabled={identityLoading}
                          onClick={async () => {
                            setIdentityLoading(true);
                            setError(null);
                            try {
                              const token = localStorage.getItem('token');
                              const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/applications/${id}/identity/start`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              const data = await res.json();
                              if (!res.ok) throw new Error(data.error || 'Failed to start verification');
                              if (data.client_secret && STRIPE_PUBLISHABLE_KEY) {
                                const stripeInstance = await loadStripe(STRIPE_PUBLISHABLE_KEY);
                                const { error: stripeError } = await stripeInstance.verifyIdentity(data.client_secret);
                                if (stripeError) {
                                  setError(stripeError.message);
                                }
                                await fetchApplication();
                              }
                            } catch (err) {
                              setError(err.message);
                            } finally {
                              setIdentityLoading(false);
                            }
                          }}
                        >
                          {identityLoading ? 'Starting...' : 'Try again'}
                        </button>
                      </div>
                    ) : (
                      <div>
                        {application?.identity_status === 'failed' && (
                          <p style={{ margin: '0 0 0.5rem', color: '#dc2626', fontWeight: 500 }}>❌ Previous verification was unsuccessful. Please try again.</p>
                        )}
                        <p className="status-text" style={{ margin: '0 0 0.75rem' }}>
                          Upload a government-issued ID and take a quick selfie. This confirms you are who you say you are and helps protect landlords from fraud.
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <button
                            className="primary-button"
                            disabled={identityLoading}
                            onClick={async () => {
                              setIdentityLoading(true);
                              setError(null);
                              try {
                                const token = localStorage.getItem('token');
                                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/applications/${id}/identity/start`, {
                                  method: 'POST',
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                const data = await res.json();
                                if (!res.ok) throw new Error(data.error || 'Failed to start verification');
                                if (data.client_secret && STRIPE_PUBLISHABLE_KEY) {
                                  const stripeInstance = await loadStripe(STRIPE_PUBLISHABLE_KEY);
                                  const { error: stripeError } = await stripeInstance.verifyIdentity(data.client_secret);
                                  if (stripeError) {
                                    setError(stripeError.message);
                                  }
                                  // Always refresh after modal closes
                                  await fetchApplication();
                                }
                              } catch (err) {
                                setError(err.message);
                              } finally {
                                setIdentityLoading(false);
                              }
                            }}
                          >
                            {identityLoading ? 'Starting verification...' : application?.identity_status === 'failed' ? 'Try again' : 'Verify my identity'}
                          </button>
                          <span className="camera-trouble-wrap">
                            <button
                              type="button"
                              className="status-pill warning"
                              style={{ fontSize: '0.75rem', cursor: 'pointer', border: 'none' }}
                              onClick={(e) => { e.stopPropagation(); const el = e.currentTarget.nextElementSibling; el.classList.toggle('camera-hint-visible'); }}
                            >
                              Camera trouble?
                            </button>
                            <div className="camera-hint-box">
                              <p style={{ margin: '0 0 0.5rem', fontWeight: 600 }}>Camera not working?</p>
                              <p style={{ margin: '0 0 0.5rem' }}>
                                <strong>On mobile:</strong> Make sure you're using Safari (iPhone) or Chrome (Android) — not an in-app browser from Telegram, Instagram, etc. Copy the link and paste it into your browser.
                              </p>
                              <p style={{ margin: '0 0 0.5rem' }}>
                                <strong>On desktop:</strong> Allow camera access when prompted, or try a different browser like Firefox or Chrome.
                              </p>
                              <button
                                type="button"
                                className="secondary-button"
                                style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }}
                                onClick={() => { navigator.clipboard.writeText(window.location.href).then(() => alert('Link copied! Paste it in Safari or Chrome.')).catch(() => {}); }}
                              >
                                Copy this page link
                              </button>
                            </div>
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Credit report: TU purchase option */}
                {step.key === 'credit' && (
                  <div className="dashboard-section" style={{ marginTop: '1rem', padding: '1rem', borderLeft: `4px solid ${tuReport?.status === 'completed' ? '#16a34a' : '#2563eb'}` }}>
                    {tuReport?.status === 'completed' ? (
                      <div>
                        <p style={{ margin: 0, fontWeight: 500, color: '#16a34a' }}>✅ TransUnion credit report purchased</p>
                        <p className="status-text" style={{ marginTop: '0.25rem' }}>Your credit report has been submitted to the landlord for review.</p>
                      </div>
                    ) : tuReport?.status === 'pending' ? (
                      <div>
                        <p style={{ margin: 0, fontWeight: 500, color: '#d97706' }}>⏳ Payment not completed</p>
                        <p className="status-text" style={{ marginTop: '0.25rem' }}>It looks like the payment window was closed before completing. You can try again below.</p>
                        <button
                          className="primary-button"
                          style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}
                          disabled={tuLoading}
                          onClick={async () => {
                            setTuLoading(true);
                            setError(null);
                            try {
                              const token = localStorage.getItem('token');
                              const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenant/tu-reports/purchase/${id}`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              if (!res.ok) {
                                const text = await res.text();
                                let msg = 'Failed to start purchase';
                                try { msg = JSON.parse(text).error || msg; } catch (_e) { /* */ }
                                throw new Error(msg);
                              }
                              const data = await res.json();
                              if (data.url) window.location.href = data.url;
                            } catch (err) {
                              setError(err.message);
                            } finally {
                              setTuLoading(false);
                            }
                          }}
                        >
                          {tuLoading ? 'Redirecting...' : 'Try again — $29.99 CAD'}
                        </button>
                      </div>
                    ) : (
                      <div>
                        <p style={{ margin: 0, fontWeight: 600, fontSize: '0.95rem' }}>Purchase a TransUnion credit report</p>
                        <p className="status-text" style={{ marginTop: '0.25rem', marginBottom: '0.75rem' }}>
                          Get an official credit report pulled directly from TransUnion. The report is shared with the landlord as part of your application.
                        </p>
                        <button
                          className="primary-button"
                          disabled={tuLoading}
                          onClick={async () => {
                            setTuLoading(true);
                            setError(null);
                            try {
                              const token = localStorage.getItem('token');
                              const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenant/tu-reports/purchase/${id}`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              if (!res.ok) {
                                const text = await res.text();
                                let msg = 'Failed to start purchase';
                                try { msg = JSON.parse(text).error || msg; } catch (_e) { /* non-JSON response */ }
                                throw new Error(msg);
                              }
                              const data = await res.json();
                              if (data.url) window.location.href = data.url;
                            } catch (err) {
                              setError(err.message);
                            } finally {
                              setTuLoading(false);
                            }
                          }}
                        >
                          {tuLoading ? 'Redirecting...' : 'Purchase credit report — $29.99 CAD'}
                        </button>
                      </div>
                    )}
                    {!tuReport?.status || tuReport?.status === 'failed' ? (
                      <p className="status-text" style={{ marginTop: '0.75rem', borderTop: '1px solid #e5e7eb', paddingTop: '0.75rem' }}>
                        Or upload your own credit report below.
                      </p>
                    ) : null}
                  </div>
                )}

                {step.docs.map((docType) => {
                  const docLabel = documentTypes.find((doc) => doc.id === docType)?.label || docType;
                  return (
                    <div key={docType} className="form-group document-upload-item">
                      <label htmlFor={docType}>{docLabel}:</label>
                      {getDocsForType(docType).length > 0 && (
                        <ul className="document-list" style={{ marginBottom: '0.75rem' }}>
                          {getDocsForType(docType).map((doc) => (
                            <li key={doc.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                              <button type="button" className="link-button" onClick={() => handleDownload(doc.id)}>
                                {doc.original_filename}
                              </button>
                              <button type="button" className="ghost-button ghost-button--tiny" onClick={() => handleDelete(doc.id, docType)}>
                                Delete
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="upload-controls">
                        <label className="secondary-button" htmlFor={docType}>
                          {isMobile ? 'Take photo or choose file' : 'Choose file'}
                          <input
                            type="file"
                            id={docType}
                            name={docType}
                            accept="image/*,application/pdf"
                            capture={isMobile ? 'environment' : undefined}
                            onChange={handleFileChange}
                          />
                        </label>
                        <span className="file-name">{files[docType]?.name || 'No file chosen'}</span>
                      </div>
                      {uploadStatus[docType] && <p className={`upload-status ${uploadStatus[docType].toLowerCase()}`}>{uploadStatus[docType]}</p>}
                    </div>
                  );
                })}

                <div className="form-actions" style={{ justifyContent: 'space-between' }}>
                  <button
                    type="button"
                    className="secondary-button"
                    disabled={!prevStep}
                    onClick={() => prevStep && handleStepChange(prevStep.key)}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="primary-button"
                    onClick={() => {
                      if (nextStep) {
                        handleStepChange(nextStep.key);
                        return;
                      }
                      navigate(`/tenant/application/${id}`, {
                        state: { notice: 'Your application was submitted. You can upload documents below or come back anytime.' }
                      });
                    }}
                  >
                    {nextStep ? `Next: ${nextStep.title}` : 'All done'}
                  </button>
                </div>
              </div>
            )
          ))}
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button
          onClick={() => navigate(`/tenant/application/${id}`, {
            state: { notice: 'Your application was submitted. You can upload documents below or come back anytime.' }
          })}
          className="primary-button"
        >
          Finish & View Application
        </button>
      </div>
    </main>
  );
}

export default DocumentUpload;
